<script setup lang="ts">
// Mascotte du chat : une boule brillante avec deux yeux.
// `sombre` : boule noire aux yeux blancs (fonds clairs) plutôt que blanche aux
// yeux noirs (sur photo/footer) — indépendant de ce qui se passe dans le chat.
// `alerte` : signale un évènement SANS changer la couleur de la boule — yeux
// rouges qui clignotent + légère secousse pour un message non lu ; regard en
// l'air + respiration lente pendant que l'équipe écrit.
// `regardeBulle` : les yeux se tournent vers la bulle d'accueil (à gauche) et la boule sautille.
// `saute` : petit sautillement permanent, sans lien avec la bulle d'accueil —
// pour qu'on remarque le bouton de chat même en dehors des 4 s d'accueil
// (Karim 2026-10-12, donoor.org uniquement).
withDefaults(
  defineProps<{
    regardeBulle?: boolean;
    sombre?: boolean;
    alerte?: "message" | "ecrit";
    saute?: boolean;
  }>(),
  { sombre: false, alerte: undefined, saute: false },
);

const uid = useId();
</script>

<template>
  <svg
    class="mascotte"
    :class="[
      { 'mascotte--sombre': sombre, 'mascotte--bulle': regardeBulle, 'mascotte--saute': saute },
      alerte ? `mascotte--${alerte}` : null,
    ]"
    viewBox="0 0 64 64"
    aria-hidden="true"
  >
    <defs>
      <radialGradient :id="`${uid}-corps`" cx="38%" cy="30%" r="75%">
        <stop class="mascotte__teinte-1" offset="0" />
        <stop class="mascotte__teinte-2" offset=".5" />
        <stop class="mascotte__teinte-3" offset="1" />
      </radialGradient>
      <radialGradient :id="`${uid}-reflet`" cx="50%" cy="50%" r="50%">
        <stop offset="0" stop-color="#fff" stop-opacity=".95" />
        <stop offset="1" stop-color="#fff" stop-opacity="0" />
      </radialGradient>
    </defs>
    <g class="mascotte__corps">
      <circle cx="32" cy="32" r="32" :fill="`url(#${uid}-corps)`" />
      <ellipse
        class="mascotte__reflet"
        cx="23"
        cy="17"
        rx="11"
        ry="7"
        :fill="`url(#${uid}-reflet)`"
        transform="rotate(-25 23 17)"
      />
      <!-- Trois couches pour les yeux, chacune avec UN seul rôle : le survol et
           la pose ne font que des transitions, le regard et le clignement que
           des animations. Couper une animation pour poser un transform fait
           sauter les yeux ; ici on se contente de la mettre en pause. -->
      <g class="mascotte__survol">
        <g class="mascotte__pose">
          <g class="mascotte__regard">
            <g class="mascotte__yeux">
              <rect class="mascotte__oeil" x="21.5" y="23" width="7" height="15" rx="3.5" />
              <rect class="mascotte__oeil" x="35.5" y="23" width="7" height="15" rx="3.5" />
              <circle class="mascotte__eclat" cx="24.3" cy="26.6" r="1.3" />
              <circle class="mascotte__eclat" cx="38.3" cy="26.6" r="1.3" />
            </g>
          </g>
        </g>
      </g>
    </g>
  </svg>
</template>

<style scoped>
.mascotte {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

/* Couleurs en CSS (et non en attributs) pour que le passage clair ↔ sombre
   se fasse en fondu au défilement. */
.mascotte__teinte-1,
.mascotte__teinte-2,
.mascotte__teinte-3,
.mascotte__oeil,
.mascotte__eclat,
.mascotte__reflet {
  transition: stop-color 0.35s ease, fill 0.35s ease, opacity 0.35s ease;
}

.mascotte__teinte-1 { stop-color: #fff; }
.mascotte__teinte-2 { stop-color: #f1f2f5; }
.mascotte__teinte-3 { stop-color: #c8cbd3; }
.mascotte__oeil { fill: #111; }
.mascotte__eclat { fill: #fff; }

.mascotte--sombre .mascotte__teinte-1 { stop-color: #2b2b2e; }
.mascotte--sombre .mascotte__teinte-2 { stop-color: #0c0c0d; }
.mascotte--sombre .mascotte__teinte-3 { stop-color: #000; }
.mascotte--sombre .mascotte__reflet { opacity: 0.16; }
.mascotte--sombre .mascotte__oeil { fill: #fff; }
.mascotte--sombre .mascotte__eclat { opacity: 0; }

/* « L'équipe écrit » reste un changement de couleur franc (pas une alerte
   urgente à distinguer visuellement d'un message) : la boule passe au bleu
   Donoor, comme avant. */
.mascotte--ecrit .mascotte__teinte-1 { stop-color: #4a72e0; }
.mascotte--ecrit .mascotte__teinte-2 { stop-color: #0c3097; }
.mascotte--ecrit .mascotte__teinte-3 { stop-color: #081f63; }
.mascotte--ecrit .mascotte__reflet { opacity: 0.35; }
.mascotte--ecrit .mascotte__oeil { fill: #fff; }
.mascotte--ecrit .mascotte__eclat { opacity: 0; }

/* Nouveau message : la boule GARDE sa couleur habituelle (claire ou sombre
   selon la page) — seuls les yeux deviennent rouges et clignotent, comme un
   voyant d'alerte. Plus visible qu'une boule entièrement rouge qui se
   confondait avec le reste de la page, et ça reste identifiable comme
   « Donoor » au premier coup d'œil. Priorité sur `--sombre` : déclaré après
   dans la feuille de style, à spécificité égale. */
.mascotte--message .mascotte__oeil {
  fill: #ff3b30;
  animation: mascotte-clignote 1.6s ease-in-out infinite;
}
.mascotte--message .mascotte__eclat { opacity: 0; }

.mascotte__corps {
  transform-box: view-box;
  transform-origin: 32px 64px;
}

.mascotte__survol,
.mascotte__pose {
  transform-box: fill-box;
  transform-origin: center;
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.mascotte__regard {
  animation: mascotte-regard 9s ease-in-out infinite;
}

.mascotte__yeux {
  transform-box: fill-box;
  transform-origin: center;
  animation: mascotte-cligne 4.2s infinite;
}

/* Dès qu'une pose ou un survol prend la main, le regard qui balaie se fige
   là où il est (pause, pas arrêt) : aucun saut, ni à l'entrée ni à la sortie.
   PAS `--saute` : ce sautillement n'a pas de direction à regarder, les yeux
   continuent de se balader normalement pendant qu'elle sautille. */
.mascotte--bulle .mascotte__regard,
.mascotte--message .mascotte__regard,
.mascotte--ecrit .mascotte__regard,
.mascotte:hover .mascotte__regard {
  animation-play-state: paused;
}

/* Le clignotement rouge remplace le clignement normal, pour ne pas les cumuler. */
.mascotte--message .mascotte__yeux {
  animation: none;
}

/* Bulle d'accueil ouverte : les yeux se tournent vers elle, la boule sautille. */
.mascotte--bulle .mascotte__pose {
  transform: translate(-5px, 1px);
}

.mascotte--bulle .mascotte__corps {
  animation: mascotte-saut 2.4s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
}

/* Sautillement permanent (hors bulle d'accueil) : UN saut à la fois, déclenché
   par le composant parent à intervalle aléatoire (`sautille`, pas de boucle
   CSS `infinite`) — un rythme parfaitement régulier se voit et lasse. */
.mascotte--saute .mascotte__corps {
  animation: mascotte-saut 1.1s cubic-bezier(0.34, 1.56, 0.64, 1) 1;
}

/* Nouveau message : yeux grands ouverts, secousse toutes les 3 s pour attirer l'œil. */
.mascotte--message .mascotte__pose {
  transform: scale(1.12);
}

.mascotte--message .mascotte__corps {
  animation: mascotte-secousse 3s ease-in-out infinite;
}

/* L'équipe écrit : regard en l'air, respiration lente, comme en attente. */
.mascotte--ecrit .mascotte__pose {
  transform: translate(2px, -3px);
}

.mascotte--ecrit .mascotte__corps {
  animation: mascotte-attente 1.6s ease-in-out infinite;
}

/* Survol : la boule lève les yeux vers le curseur et les ouvre plus grand. */
.mascotte:hover .mascotte__survol {
  transform: translate(0, -3px) scale(1.12);
}

@keyframes mascotte-secousse {
  0%, 70%, 100% { transform: rotate(0deg); }
  74% { transform: rotate(-9deg); }
  80% { transform: rotate(8deg); }
  86% { transform: rotate(-6deg); }
  92% { transform: rotate(3deg); }
}

@keyframes mascotte-attente {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
}

@keyframes mascotte-cligne {
  0%, 92%, 100% { transform: scaleY(1); }
  95% { transform: scaleY(0.12); }
}

/* Fondu doux entre rouge vif et rose clair — un pouls, pas un clignotement
   mécanique. `ease-in-out` ralentit aux deux bouts, comme une respiration. */
@keyframes mascotte-clignote {
  0%, 100% { fill: #ff3b30; }
  50% { fill: #ffbbb6; }
}

@keyframes mascotte-regard {
  0%, 30%, 100% { transform: translate(0, 0); }
  38%, 52% { transform: translate(-4px, 1px); }
  60%, 72% { transform: translate(3px, -1px); }
}

@keyframes mascotte-saut {
  0%, 40%, 100% { transform: translateY(0) scale(1, 1); }
  8% { transform: translateY(0) scale(1.06, 0.92); }
  18% { transform: translateY(-5px) scale(0.96, 1.05); }
  28% { transform: translateY(0) scale(1.04, 0.95); }
}

@media (prefers-reduced-motion: reduce) {
  .mascotte__regard,
  .mascotte__yeux,
  .mascotte--bulle .mascotte__corps,
  .mascotte--saute .mascotte__corps,
  .mascotte--message .mascotte__corps,
  .mascotte--ecrit .mascotte__corps {
    animation: none;
  }
  .mascotte--message .mascotte__oeil {
    /* Reste rouge, fixe : toujours visible comme alerte, sans clignoter. */
    animation: none;
  }
}
</style>
